
import { defineComponent, PropType } from 'vue';
import ConfirmationOverlay from '@/components/ConfirmationOverlay.vue';
import DotSubmissionConfirmation from './DotSubmissionConfirmation.vue';
import TrailerAssessment from '@/types/TrailerAssessment';
import TrailerAssessmentType from '@/types/TrailerAssessmentType';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import { omit, pickBy } from 'lodash';
import { mapGetters } from 'vuex';
import { ActionTypes } from '@/store/actionTypes';
import { isNullOrUndefined } from '@/shared/helpers';
import { DateTime } from 'luxon';
import FbaApiError from '@/types/FbaApiError';
import { MutationTypes } from '@/store/mutationTypes';
import { AssociationType } from '@/types/AssociationType';
import useAssociation from '@/shared/composables/useAssociation';
import globalConstants from '@/shared/globalConstants';

const listItems: {
    [T in keyof TrailerAssessment]: string;
} = {
    brakes: 'Brakes',
    tires: 'Tires & Wheels',
    lights: 'Lights',
    damage: 'Body Damage',
    kingpinLanding: 'Kingpin & Landing Gear',
    airlinesElectrical: 'Airlines & Electrical',
    other: 'Other',
};

export default defineComponent({
    components: {
        ConfirmationOverlay,
        SomethingWrongOverlay,
        DotSubmissionConfirmation,
    },
    data(): {
        isLoading: boolean;
        showDotConfirmation: boolean;
    } {
        return {
            isLoading: false,
            showDotConfirmation: false,
        };
    },
    props: {
        association: {
            type: String as PropType<AssociationType>,
            required: true,
        },
    },
    setup() {
        const {
            result: associationResult,
            error: associationError,
            isLoading: associationIsLoading,
            updateAssociation,
        } = useAssociation();

        return {
            associationResult,
            associationError,
            associationIsLoading,
            updateAssociation,
        };
    },
    updated(): void {
        this.handleListLength();
    },
    computed: {
        ...mapGetters({
            selectedTrailer: 'selectedTrailer',
            trailerAssessment: 'trailerAssessment',
            bestAvailableLocation: 'getPinnedBestAvailableLocation',
        }),
        confirmationShown(): boolean {
            return this.$route.query.confirmation === 'confirmdamage';
        },
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
        hasError() {
            return !isNullOrUndefined(this?.error?.message);
        },
        isAssessmentForPreviousTrailer() {
            return (
                !isNullOrUndefined(this.trailerNumber) &&
                !isNullOrUndefined(this.selectedTrailer) &&
                this.trailerNumber !== this.selectedTrailer.trailerNumber
            );
        },
        isAssessmentTrailerDotExpired() {
            return (
                this.isAssessmentForPreviousTrailer === false &&
                this.selectedTrailer?.isDotInspectionExpired === true
            );
        },
        recordedTypes(): TrailerAssessmentType[] {
            const types = pickBy(
                this.trailerAssessment,
                (type) => type?.typeHasDamage
            );
            return Object.values(types);
        },
        trailerNumber() {
            return this.$route.params.trailerNumber as string;
        },
        company() {
            return this.$route.params.company as string;
        },
    },
    methods: {
        onAccept() {
            if (this.association !== AssociationType.DETACH) {
                this.$router.push({
                    query: {
                        ...this.$route.query,
                        confirmation: 'acceptwithdamage',
                    },
                });

                return;
            } else this.showDotConfirmation = true;
        },

        async submitAssessment(): Promise<void> {
            const response = await this.confirmTrailer();

            if (response === false || isNullOrUndefined(response)) {
                this.$store.dispatch(ActionTypes.CLEAR_QUERY_PARAM_AND_ROUTE, {
                    routeToName: 'TrailerAssessment',
                    omitParam: 'confirmation',
                });
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    new FbaApiError('Trailer attach/detach association failed')
                );
                return;
            }

            this.$router.push({
                name: 'AssociateTrailerSuccess',
                query: {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    association: this.association,
                    hasBodyDamage: 'true',
                },
            });
        },
        onReject(): void {
            this.$store.dispatch(ActionTypes.CLEAR_QUERY_PARAM_AND_ROUTE, {
                routeToName: 'TrailerAssessment',
                omitParam: 'confirmation',
            });
        },
        goBackToBeginningOfAssessment(): void {
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.$router.push({
                name: 'TrailerAssessment',
                query: omit(this.$route.query, 'confirmation'),
            });
            this.$router.go(0);
        },
        listItemTitle(type: keyof TrailerAssessment): string {
            return listItems[type] as string;
        },
        handleListLength(): void {
            // handles known deficiency in flex layout to wrap and center list items

            if (this.recordedTypes?.length > 4) {
                const container = document.getElementById('fbx-damage-list');

                if (container instanceof HTMLElement)
                    container.classList.add('extra-wide');
            }
        },
        async postAssessment() {
            const hasPhotos = this.$store.getters.trailerPhotosCount > 0;
            const assessmentResponse = await this.$store.dispatch(
                ActionTypes.POST_TRAILER_ASSESSMENT,
                {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    eventName: this.association,
                    damageSignatureRecordedTimestamp: DateTime.now().toISO(),
                    hasPhotos,
                    source: globalConstants.trailer.trailerAssessment.source,
                    entryPoint:
                        globalConstants.trailer.trailerAssessment.entryPoints
                            .attach,
                }
            );

            if (assessmentResponse instanceof FbaApiError) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    assessmentResponse as FbaApiError
                );
                return false;
            }

            if (
                assessmentResponse?.didSucceed &&
                assessmentResponse?.assessmentId &&
                hasPhotos
            ) {
                await this.$store.dispatch(
                    ActionTypes.SUBMIT_TRAILER_PHOTOS,
                    assessmentResponse.assessmentId
                );
            }

            return assessmentResponse?.didSucceed;
        },
        async confirmTrailer(): Promise<boolean> {
            this.isLoading = true;
            const assessmentRes = await this.postAssessment();

            if (!assessmentRes) {
                this.isLoading = false;
                return false;
            }

            await this.updateAssociation(
                this.association,
                this.trailerNumber,
                this.company,
                this.bestAvailableLocation
            );

            this.isLoading = false;
            return assessmentRes && (this.associationResult ?? false);
        },
    },
});
